import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ThtLogo from "./tht_logo.png";
import { GroundEdge, Sprout } from "./ThtParts";
import "./ThtLayout.css";
import "./ThtNatural.css";

type NavItem = { label: string; en: string; to: string; children?: { label: string; to: string }[] };

const navItems: NavItem[] = [
  { label: "ホーム", en: "Home", to: "/" },
  { label: "ニュース", en: "News", to: "/news" },
  // 教室一覧は業務内容(スポーツクラブ)の下の階層
  { label: "業務内容", en: "Business", to: "/business", children: [{ label: "教室一覧", to: "/class" }] },
  { label: "会社案内", en: "Company", to: "/company" },
];

// 所在地とSNS(既存の会社概要ページ・教室サイトと同じ情報)
const ADDRESS = "石川県金沢市田上本町4丁目40番地";
const MAP_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`;
const INSTAGRAM_URL = "https://www.instagram.com/donichidaketaisou/";

const ease =[0.22, 1, 0.36, 1] as const;

export const ThtHeader = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const { pathname } = useLocation();
  // ホームの最上部ではヘッダーをヒーロー画像に重ねて透明にする
  const overlay = pathname === "/" && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      setShowTop(window.scrollY > 400);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ページ移動したらメニューを閉じる
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // メニューを開いている間は背景をスクロールさせない・Escで閉じる
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <header className={`tht-header${overlay ? " is-overlay" : ""}${scrolled ? " is-scrolled" : ""}${open ? " is-open" : ""}`}>
        <Link to="/" className="tht-header-logo">
          <img src={ThtLogo} alt="THT合同会社" />
        </Link>
        <div className="tht-header-actions">
          <Link to="/contact" className="tht-header-contact">
            お問い合わせ
          </Link>
          <button
            type="button"
            className="tht-menu-btn"
            aria-label={open ? "メニューを閉じる" : "メニューを開く"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {/* 外側の形:閉じているときは種、開くと葉が出て芽になる */}
            <span className="tht-menu-leaf tht-menu-leaf-l" aria-hidden="true" />
            <span className="tht-menu-leaf tht-menu-leaf-r" aria-hidden="true" />
            <span className="tht-menu-lines">
              <i />
              <i />
              <i />
            </span>
            <span className="tht-menu-label">{open ? "CLOSE" : "MENU"}</span>
          </button>
        </div>
      </header>

      {/* 全画面メニュー:常に置いておき、開閉はCSSで切り替える(閉じた後に一瞬再表示されるちらつきを防ぐため) */}
      <div className={`tht-gnav${open ? " is-open" : ""}`} aria-hidden={!open}>
        <div className="tht-gnav-decor" aria-hidden="true">
          <span className="tht-gnav-blob tht-gnav-blob-1" />
          <span className="tht-gnav-blob tht-gnav-blob-2" />
          <span className="tht-gnav-leaf tht-gnav-leaf-1" />
          <span className="tht-gnav-leaf tht-gnav-leaf-2" />
        </div>
        <GroundEdge color="#3e2e22" className="tht-gnav-ground" />

        <div className="tht-gnav-inner">
          <nav className="tht-gnav-list">
            {[...navItems, { label: "お問い合わせ", en: "Contact", to: "/contact" } as NavItem].map((item, i) => (
              <div key={item.to} className="tht-gnav-item" style={{ "--i": i } as React.CSSProperties}>
                <Link
                  to={item.to}
                  tabIndex={open ? 0 : -1}
                  className={`tht-gnav-link${pathname === item.to ? " is-current" : ""}`}
                >
                  <span className="tht-gnav-num">0{i + 1}</span>
                  <span className="tht-gnav-en">{item.en}</span>
                  <span className="tht-gnav-ja">
                    <Sprout className="tht-gnav-sprout" />
                    {item.label}
                  </span>
                </Link>
                {/* 業務内容の下の階層(教室一覧) */}
                {item.children?.map((child) => (
                  <Link
                    key={child.to}
                    to={child.to}
                    tabIndex={open ? 0 : -1}
                    className={`tht-gnav-child${pathname === child.to ? " is-current" : ""}`}
                  >
                    <span className="tht-gnav-child-branch" aria-hidden="true" />
                    {child.label}
                  </Link>
                ))}
              </div>
            ))}
          </nav>

          <div className="tht-gnav-side">
            <p className="tht-gnav-catch">
              好奇心の
              <br />
              タネをまく
            </p>
          </div>
        </div>
      </div>

      {/* ページ上部へ戻る:最上部では隠し、下にスクロールしたらふわっと出す(フッターの外に置いて常に最前面へ) */}
      <button
        type="button"
        className={`tht-pagetop${showTop && !open ? " is-visible" : ""}`}
        aria-label="ページ上部へ戻る"
        tabIndex={showTop ? 0 : -1}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      />
    </>
  );
};

export const ThtFooter = () => (
  <footer className="tht-footer">
    <GroundEdge color="#3e2e22" className="tht-footer-ground" />
    <div className="tht-footer-inner">
      <div className="tht-footer-brand">
        <img src={ThtLogo} alt="THT合同会社" className="tht-footer-logo" />
        <p className="tht-footer-copy">誰もが成長できる。場所をつくる。</p>
        <p className="tht-footer-address">〒920-1155 石川県金沢市田上本町4丁目40番地</p>
      </div>
      <nav className="tht-footer-nav">
        {[...navItems, { label: "お問い合わせ", en: "Contact", to: "/contact" } as NavItem].map((item) => (
          // 親の項目と、その下の階層(教室一覧)を1つのまとまりにして、親の真下に並べる
          <div key={item.to} className="tht-footer-group">
            <Link to={item.to}>
              <Sprout className="tht-nav-sprout" />
              {item.label}
            </Link>
            {item.children?.map((child) => (
              <Link key={child.to} to={child.to} className="tht-footer-child">
                └ {child.label}
              </Link>
            ))}
          </div>
        ))}
      </nav>
      {/* どのページからでもすぐ連絡・確認できる情報 */}
      <div className="tht-footer-info">
        <div className="tht-footer-links">
          <a href={MAP_URL} target="_blank" rel="noopener noreferrer">
            地図を見る
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
            Instagram（土日だけ体操教室）
          </a>
        </div>
      </div>
    </div>
    <div className="tht-footer-bottom">
      <Link to="/privacypolicy">プライバシーポリシー</Link>
      <span>&copy; {new Date().getFullYear()} THT合同会社</span>
    </div>
  </footer>
);

// ページを読み込んだときの演出:種がまかれ、そこから光が広がってページが現れる(サイトを開いた最初の1回だけ)
// 判定は1回だけ行い、結果を使い回す(開発中のReactは同じ処理を2回呼ぶため、毎回判定すると2回目で「もう出した」になってしまう)
let loaderDecision: boolean | null = null;
const shouldPlayLoader = () => {
  if (loaderDecision !== null) return loaderDecision;
  loaderDecision = (() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    try {
      if (sessionStorage.getItem("tht-loader-played")) return false;
      sessionStorage.setItem("tht-loader-played", "1");
    } catch {
      // 保存できない環境でも、このページを開いている間は1回だけ
    }
    return true;
  })();
  return loaderDecision;
};


// 演出の進行(ミリ秒)
const LOADER_ENDING_AT = 2350; // この時点でヒーローの手のアニメーションを始める(演出が消え始める直前)
const LOADER_HIDE_AT = 2500; // この時点で演出を消し始める
let loaderActive = false;
export const LOADER_ENDING_EVENT = "tht-loader-ending";
/** 読み込み時の演出が再生中か(ヒーローの手のアニメーションを、演出の終わりに合わせるため) */
export const isLoaderActive = () => loaderActive;


export const ThtLoader = () => {
  const [fading, setFading] = useState(false);
  const [show, setShow] = useState(() => {
    loaderActive = shouldPlayLoader();
    return loaderActive;
  });

  useEffect(() => {
    if (!show) return;
    let remove: ReturnType<typeof setTimeout>;
    const ending = setTimeout(() => window.dispatchEvent(new Event(LOADER_ENDING_EVENT)), LOADER_ENDING_AT);
    const hide = setTimeout(() => {
      setFading(true); // CSSでふわっと消す
      remove = setTimeout(() => setShow(false), 1000);
      loaderActive = false;
      loaderDecision = false; // 再生し終えたら、ページを移動しても二度と出さない
    }, LOADER_HIDE_AT);
    return () => {
      clearTimeout(ending);
      clearTimeout(hide);
      clearTimeout(remove);
    };
  }, [show]);

  if (!show) return null;
  // シンプルな演出:サイトの地色の上で、種が1つ落ちて芽が育ち、そこから円形に開いてホームへつながる
  return (
    <div className={`tht-loader tht-loader-simple${fading ? " is-fading" : ""}`} aria-hidden="true">
      <div className="tht-loader-stage">
        {/* 足元の小さな土(種が落ちる場所) */}
        <motion.svg
          className="tht-loader-mound"
          viewBox="0 0 160 40"
          initial={{ opacity: 0, scaleX: 0.6 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.1, ease }}
        >
          <path d="M6 38 C30 16 60 10 80 10 C100 10 130 16 154 38 Z" fill="#b5651d" />
          <path d="M30 30 C52 18 70 15 80 15 C94 15 112 19 130 28" fill="none" stroke="#c98048" strokeWidth="3" strokeLinecap="round" />
        </motion.svg>
        <svg className="tht-loader-sprout" viewBox="0 0 60 80">
          <motion.path
            d="M30 78 C30 62 29 50 30 34"
            fill="none"
            stroke="#5cae5a"
            strokeWidth="4"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.85, ease, opacity: { duration: 0.01, delay: 0.85 } }}
          />
          <motion.path
            d="M30 38 C22 37 12 30 10 18 C20 19 28 26 30 38 Z"
            fill="#7cc576"
            style={{ originX: "30px", originY: "38px" }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 12, delay: 1.2 }}
          />
          <motion.path
            d="M30 38 C38 37 48 30 50 18 C40 19 32 26 30 38 Z"
            fill="#5cae5a"
            style={{ originX: "30px", originY: "38px" }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 12, delay: 1.3 }}
          />
        </svg>
        <motion.span
          className="tht-loader-seed"
          initial={{ y: "-55vh", rotate: -300 }}
          animate={{ y: ["-55vh", "0vh", "-3vh", "0vh"], rotate: [-300, 0, -12, 0] }}
          transition={{ duration: 0.85, times: [0, 0.65, 0.82, 1], ease: "easeIn" }}
        />
      </div>
      {/* 芽が育ったあと、キャッチコピーが浮かぶ(ヒーローの文字への予告) */}
      <motion.p
        className="tht-loader-copy"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.45, ease }}
      >
        好奇心のタネをまく
      </motion.p>
    </div>
  );
};
